import { ViewState } from 'react-map-gl/mapbox';
import { atom, selector } from 'recoil';
import { PointFeature } from 'supercluster';
import { MAP_CONFIGURATION, ROAD_MAP_TYPE } from '../constants/map';
import { MapType } from '../types/app';
import { isValidPosition } from '../utils/map';
import { localStorageEffect, persistedAtom } from '../utils/persistence';
import { jobsState } from './jobStates';
import { usersState } from './userStates';

export const mapTypeState = persistedAtom<MapType>('app.map.type', ROAD_MAP_TYPE);
export const mapStyleState = selector({
  key: 'app.map.styles',
  get: ({ get }) => MAP_CONFIGURATION[get(mapTypeState)].style,
});
export const mapPaddingState = atom({
  key: 'app.map.padding',
  default: 0,
});

export const mapViewState = atom<ViewState>({
  key: 'map.view',
  default: {
    longitude: -100.38806,
    latitude: 20.58806,
    zoom: 14,
  } as ViewState,
  effects: [localStorageEffect],
});

export const showTrafficLayerState = persistedAtom('app.map.layer.traffic.show', false);

export const mapUserGeoJsonState = selector<
  PointFeature<{
    cluster: boolean;
    id: string;
    point_count: number;
  }>[]
>({
  key: 'app.map.users.geojson',
  get: ({ get }) => {
    let users = get(usersState)?.items;
    if (!users) return [];

    const userMarkers = users
      .filter((user) => isValidPosition(user.lastPosition?.latitude, user.lastPosition?.longitude))
      .map((user) => ({
        user,
        geometry: {
          type: 'Point',
          coordinates: [user.lastPosition?.longitude, user.lastPosition?.latitude],
        },
      }));

    return userMarkers.map((userMarker) => ({
      type: 'Feature',
      properties: { cluster: false, id: userMarker.user._id, point_count: 0 },
      geometry: userMarker.geometry as GeoJSON.Point,
    }));
  },
});

export const mapJobGeoJsonState = selector<PointFeature<{ cluster: boolean; id: string; point_count: number }>[]>({
  key: 'app.map.jobs.geojson',
  get: ({ get }) => {
    let jobs = get(jobsState)?.items;
    if (!jobs) return [];

    const jobMarkers = jobs
      .filter((job) => isValidPosition(job.latitude, job.longitude))
      .map((job) => ({
        job,
        geometry: {
          type: 'Point',
          coordinates: [job.longitude, job.latitude],
        },
      }));

    return jobMarkers.map((jobMarker) => ({
      type: 'Feature',
      properties: { cluster: false, id: jobMarker.job._id, point_count: 0 },
      geometry: jobMarker.geometry as GeoJSON.Point,
    }));
  },
});
